import { useEffect, useMemo, useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { SegInput } from "../../../SegUI/SegInput/SegInput";
import { SegButton } from "../../../SegUI/SegButton/SegButton";
import { SegSelect, SegSelectOption } from "../../../SegUI/SegSelect/SegSelect";
import { SegTooltip } from "../../../SegUI/SegTooltip/SegTooltip";
import { EmpresaProps } from "../../../../Services/Providers/useConfiguracionEmpresa";
import { useCotizacionStore } from "../../../../Services/Providers/CotizacionStore";
import { useEmisionStore } from "../../../../Services/Providers/EmisionStore";
import { useConfig } from "../../../../context/ConfigContext";
import { getRecaptchaKey } from "../../../../Services/API/helper";
import {
  obtenerProvincias,
  obtenerLocalidades,
} from "../../../../Services/API/bff/auto";
import { autoViaBffDesdeConfig } from "../../../../Services/API/bff/auto/flag";
import { BloqueVehiculo, ModoVehiculo, RubroVehiculo } from "./BloqueVehiculo";
import {
  CampoCotizacion,
  ValoresCotizacion,
  anioMaximoCobertura,
  camposRequeridos,
  valoresIniciales,
  validarCampo,
} from "./validaciones";
import { getPasoVehiculo } from "../../../../utils/getRubro";
import { conAsterisco } from "../../../SegUI/SegRequerido/conAsterisco";
import { useTextosCotizadorAuto } from "../../../../Services/ventaPresencial/textosAsesor";
import { inicioCotizacion } from "../../../../Services/ventaPresencial/rutaVendedor";

interface DatosCotizacionProps {
  rubro: RubroVehiculo;
  config: EmpresaProps;
  /** Auto confirma el vehículo acá; Moto ya lo resolvió en el paso anterior. */
  incluirVehiculo: boolean;
}

const OPCIONES_CUANDO_CONTRATA: SegSelectOption[] = [
  { value: "ahora", label: "Ahora" },
  { value: "7-dias", label: "En 7 días" },
  { value: "15-dias", label: "En 15 días" },
  { value: "no-se", label: "No lo sé" },
  { value: "no-quiero", label: "No quiero asegurar" },
];

/**
 * El campo "¿Cuándo asegurás?" está oculto por ahora: todo entra como "Ahora"
 * automáticamente (alimenta la validación y el ruteo del resultado).
 */
const CUANDO_POR_DEFECTO = OPCIONES_CUANDO_CONTRATA[0];

const GENEROS: SegSelectOption[] = [
  { value: "F", label: "FEMENINO" },
  { value: "M", label: "MASCULINO" },
];

const Contenedor = styled.div`
  background: #f6f8fb;
  padding-bottom: 32px;

  /* Los sub-labels internos de SegInput (Día/Mes/Año, Cód. Área, Número) usan
     la .form-label de Bootstrap (16px, negra). Acá se los iguala al formato de
     los labels del paso (el styled Etiqueta): 13.5px, peso 500, gris #4a5468.
     Scopeado al paso 2 para no tocar el .form-label del resto del sitio. */
  .form-label {
    font-size: 13.5px;
    font-weight: 500;
    color: #4a5468 !important;
    margin-bottom: 6px;
  }

  /* Los .form-control de Bootstrap quedan en 42px por su padding, más altos que
     los selects (react-select "md" = 38px). Se igualan al alto del select en el
     paso 2 (texto, numéricos DD/MM/AAAA y prefijo/número del teléfono). Van a 36px
     porque el .input-group les suma su borde (1px arriba+abajo) → caja total 38px. */
  .input-group .form-control,
  .input-group .input-group-text {
    height: 36px;
  }
`;

const Columna = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 24px 16px 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Titulo = styled.div`
  h2 {
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin: 0 0 4px;
  }
  p {
    margin: 0;
    font-size: 14.5px;
    color: #4a5468;
  }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #dde3ec;
  border-radius: 10px;
  padding: 18px;
`;

/** Aviso informativo cuando la patente buscada no trajo datos del datacar. */
const AvisoPatente = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #eef4ff;
  border: 1px solid #cfe0ff;
  color: #1b3a6b;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 13.5px;
  line-height: 1.35;

  svg {
    flex: none;
    margin-top: 1px;
  }
`;

const Grupo = styled.fieldset`
  border: 0;
  margin: 0;
  padding: 0;
`;

const Leyenda = styled.legend`
  padding: 0;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-weight: 700;
  color: #7a8394;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-bottom: 10px;
  float: none;
`;

const Etiqueta = styled.span`
  font-size: 13.5px;
  font-weight: 500;
  color: #4a5468;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
`;

/** Asterisco de campo obligatorio, pegado al texto del label. */
const MensajeError = styled.span`
  font-size: 12.5px;
  color: #d1424f;
  font-weight: 500;
  min-height: 18px;
  display: block;
`;

const Ayuda = styled.span`
  font-size: 12.5px;
  color: #7a8394;
  display: block;
`;

const Cierre = styled.div`
  background: #fff;
  border-top: 1px solid #dde3ec;
  margin-top: 18px;
  padding: 16px 0;
`;

const CierreInterno = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  /* En mobile se apilan: el primario arriba (ancho completo) y "Volver" abajo. */
  @media (max-width: 600px) {
    flex-direction: column-reverse;
    align-items: stretch;
  }
`;

const aOpciones = (items: any): SegSelectOption[] =>
  Array.isArray(items)
    ? items.map((item: any) => ({ value: item, label: item }))
    : [];

/** Dónde termina el flujo según rubro, país y cuándo piensa contratar. */
const rutaResultado = (
  rubro: RubroVehiculo,
  isUY: boolean,
  cuandoContrata?: string
): string => {
  if (rubro === "moto") {
    if (isUY) return "/resultado-del-cotizador-de-moto";
    return cuandoContrata === "ahora"
      ? "/resultado-del-cotizador-de-moto?cuandoasegura=ahora"
      : "/resultado-del-cotizador-de-moto";
  }
  return cuandoContrata === "ahora"
    ? "/resultado-del-cotizador-de-autos?cuandoasegura=ahora"
    : "/resultado-del-cotizador-de-seguros-auto";
};

export const DatosCotizacion = ({
  rubro,
  config,
  incluirVehiculo,
}: DatosCotizacionProps) => {
  const nav = useNavigate();
  const { compania } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  /** La patente buscada no trajo datos: se avisa y se carga a mano. */
  const patenteSinDatos = !!(location.state as { patenteSinDatos?: boolean })
    ?.patenteSinDatos;
  const { config: envConf } = useConfig();
  const { cotizacionValues, setCotizacionValues } = useCotizacionStore();
  // Textos del asesor solo en Auto y si la cotización arrancó en /auto/vendedor (US 23866).
  const textos = useTextosCotizadorAuto(rubro);
  const { resetEmisionValues } = useEmisionStore();
  const methods = useForm();
  const formRef = useRef<HTMLFormElement | null>(null);

  const isUY = envConf.pais === "UY";

  /**
   * Camino BFF: Segucore no resuelve localidad -> código postal, así que la
   * localidad se pide como texto libre y el CP (4 dígitos) pasa a ser un campo
   * obligatorio. La provincia sigue siendo un select (viene de lookups).
   */
  const viaBff = autoViaBffDesdeConfig(envConf);

  const [valores, setValores] = useState<ValoresCotizacion>(valoresIniciales);
  const [errores, setErrores] = useState<
    Partial<Record<CampoCotizacion, string>>
  >({});
  const [enviando, setEnviando] = useState(false);
  const [errorGeneral, setErrorGeneral] = useState("");
  const [hidratado, setHidratado] = useState(false);

  const [provincias, setProvincias] = useState<SegSelectOption[]>();
  const [localidades, setLocalidades] = useState<SegSelectOption[]>();
  const [cargandoLocalidades, setCargandoLocalidades] = useState(false);

  /**
   * El vehículo viene del datacar cuando hay patente y catálogo resuelto.
   * Si no, se carga a mano (se entró sin patente o el datacar no encontró nada).
   */
  const modoVehiculo: ModoVehiculo = useMemo(
    () =>
      cotizacionValues.patente &&
      cotizacionValues.marca?.value &&
      cotizacionValues.version?.value &&
      // Sin ids del catálogo no hay cascada posible: se carga a mano.
      !cotizacionValues.vehiculoSinIds
        ? "patente"
        : "manual",
    [
      cotizacionValues.patente,
      cotizacionValues.marca,
      cotizacionValues.version,
      cotizacionValues.vehiculoSinIds,
    ]
  );

  /**
   * El datacar encontró el vehículo pero el catálogo no lo reconoce por
   * nombre: se muestran los campos como texto, ya precargados con lo que trajo
   * la patente, para que la persona los corrija en vez de empezar de cero.
   */
  const vehiculoComoTexto = !!cotizacionValues.vehiculoSinIds;

  const campos = useMemo(
    () =>
      camposRequeridos({
        incluirVehiculo,
        modoVehiculo,
        exigirCodigoPostal: viaBff,
      }),
    [incluirVehiculo, modoVehiculo, viaBff]
  );

  /** Todos los campos requeridos válidos → habilita "Ver precios". */
  const completo = useMemo(
    () =>
      campos.every(
        (c) => !validarCampo(c, valores, { isUY, localidadLibre: viaBff })
      ),
    [campos, valores, isUY, viaBff]
  );

  /** Auto antiguo: el corte lo marca el datacar, igual que en ResultadoPatente. */
  const vehiculoAntiguo = incluirVehiculo && !!cotizacionValues.esAntiguo;
  const anioMaximo = anioMaximoCobertura(envConf.anio_gracias ?? 30);

  /* ── hidratación desde el store ── */
  useEffect(() => {
    setValores({
      marca: cotizacionValues.marca,
      modelo: cotizacionValues.modelo,
      anio: cotizacionValues.año,
      version: cotizacionValues.version,
      nombre: cotizacionValues.nombre || "",
      apellido: cotizacionValues.apellido || "",
      dia: cotizacionValues.fechaNac?.dia || "",
      mes: cotizacionValues.fechaNac?.mes || "",
      anioNac: cotizacionValues.fechaNac?.año || "",
      genero: cotizacionValues.genero,
      email: cotizacionValues.email || "",
      prefijo: isUY ? "9" : cotizacionValues.telefono?.prefijo || "",
      numero: cotizacionValues.telefono?.numero || "",
      provincia: cotizacionValues.provincia,
      localidad: cotizacionValues.localidad,
      codigoPostal: cotizacionValues.codigoPostal || "",
      // Campo oculto por ahora: todo entra como "Ahora".
      cuandoContrata: CUANDO_POR_DEFECTO,
    });
    setHidratado(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Moto resuelve el vehículo en el paso anterior: si se entra directo a esta
   * URL sin haberlo elegido, se vuelve ahí.
   */
  useEffect(() => {
    if (incluirVehiculo) return;
    if (!cotizacionValues.marca?.value || !cotizacionValues.version?.value) {
      nav(getPasoVehiculo(rubro, compania), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── errores que devuelve el paso de precios por query string ── */
  useEffect(() => {
    const error = searchParams.get("error");
    if (!error) return;
    if (error === "El Captcha no es válido") {
      setErrorGeneral(error);
      return;
    }
    if (error === "Error de conexion.") {
      setErrorGeneral(
        "Se ha producido un error de conexión. Verificá que todo esté correcto e intentá nuevamente en 30 segundos."
      );
      return;
    }
    const campo = searchParams.get("campo") as CampoCotizacion | null;
    if (campo) setErrores((prev) => ({ ...prev, [campo]: error }));
    else setErrorGeneral(error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── provincias ── */
  useEffect(() => {
    let vigente = true;
    const fetchProvincias = async () => {
      const response: any = await obtenerProvincias();
      if (vigente) setProvincias(aOpciones(response));
    };
    fetchProvincias();
    return () => {
      vigente = false;
    };
  }, []);

  /* ── localidades según provincia ── */
  useEffect(() => {
    if (!hidratado) return;
    if (viaBff) return; // sin catálogo de localidades: es texto libre
    if (!valores.provincia?.value) {
      setLocalidades(undefined);
      return;
    }
    let vigente = true;
    setCargandoLocalidades(true);
    const fetchLocalidades = async () => {
      const response: any = await obtenerLocalidades(
        valores.provincia?.value || ""
      );
      if (!vigente) return;
      const opciones = aOpciones(response);
      setLocalidades(opciones);
      setCargandoLocalidades(false);
      // La localidad guardada puede no existir en la provincia elegida.
      setValores((prev) =>
        prev.localidad?.value &&
        !opciones.some((o) => o.value === prev.localidad?.value)
          ? { ...prev, localidad: undefined }
          : prev
      );
    };
    fetchLocalidades();
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidratado, valores.provincia?.value, viaBff]);

  /* ── fecha de nacimiento en un solo campo: se tipea corrido y se muestra DD/MM/AAAA,
     guardando día/mes/año por separado para no tocar la validación (validarEdad). ── */
  const cambiarFecha = (valor: string) => {
    const d = valor.replace(/\D/g, "").slice(0, 8);
    cambiar({ dia: d.slice(0, 2), mes: d.slice(2, 4), anioNac: d.slice(4, 8) });
  };
  const fechaValor = [valores.dia, valores.mes, valores.anioNac]
    .filter(Boolean)
    .join("/");

  /**
   * El datacar puede traer un año que el catálogo de la versión ya no cotiza.
   * En ese caso se descarta para que la persona lo vuelva a elegir, igual que
   * hacía ResultadoPatente al abrir el select.
   */
  const depurarAnio = (opciones: SegSelectOption[]) => {
    setValores((prev) =>
      prev.anio?.value &&
      !opciones.some((o) => o.value === `${prev.anio?.value}`)
        ? { ...prev, anio: undefined }
        : prev
    );
  };

  /** Los campos de fecha y teléfono son varios inputs para una sola validación. */
  const campoDeClave = (clave: keyof ValoresCotizacion): CampoCotizacion => {
    if (clave === "dia" || clave === "mes" || clave === "anioNac")
      return "fechaNac";
    if (clave === "prefijo" || clave === "numero") return "telefono";
    return clave as CampoCotizacion;
  };

  const cambiar = (parcial: Partial<ValoresCotizacion>) => {
    const siguiente = { ...valores, ...parcial };
    setValores(siguiente);
    // Un campo ya marcado en rojo se revalida mientras se escribe; uno que
    // todavía no se tocó no se marca hasta el blur o el submit.
    setErrores((prev) => {
      const actualizados = { ...prev };
      (Object.keys(parcial) as (keyof ValoresCotizacion)[]).forEach((clave) => {
        const campo = campoDeClave(clave);
        if (!actualizados[campo]) return;
        const mensaje = validarCampo(campo, siguiente, { isUY, localidadLibre: viaBff });
        if (mensaje) actualizados[campo] = mensaje;
        else delete actualizados[campo];
      });
      return actualizados;
    });
  };

  /** Valida al salir del campo, no al apretar el botón. */
  const revisar = (campo: CampoCotizacion) => {
    const mensaje = validarCampo(campo, valores, { isUY, localidadLibre: viaBff });
    setErrores((prev) => {
      const actualizados = { ...prev };
      if (mensaje) actualizados[campo] = mensaje;
      else delete actualizados[campo];
      return actualizados;
    });
  };

  const enfocarPrimerError = (
    pendientes: Partial<Record<CampoCotizacion, string>>
  ) => {
    const primero = campos.find((campo) => pendientes[campo]);
    if (!primero) return;
    const nodo = formRef.current?.querySelector<HTMLElement>(
      `[data-campo="${primero}"]`
    );
    if (!nodo) return;
    nodo.scrollIntoView({ behavior: "smooth", block: "center" });
    const foco = nodo.querySelector<HTMLElement>("input,select,button");
    setTimeout(() => foco?.focus({ preventScroll: true }), 300);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorGeneral("");
    if (vehiculoAntiguo) return;

    const pendientes: Partial<Record<CampoCotizacion, string>> = {};
    campos.forEach((campo) => {
      const mensaje = validarCampo(campo, valores, { isUY, localidadLibre: viaBff });
      if (mensaje) pendientes[campo] = mensaje;
    });
    setErrores(pendientes);
    if (Object.keys(pendientes).length > 0) {
      enfocarPrimerError(pendientes);
      return;
    }

    setEnviando(true);
    const key = await getRecaptchaKey();
    grecaptcha.ready(function () {
      grecaptcha.execute(key!, { action: "submit" }).then(function (token) {
        setCotizacionValues({
          ...cotizacionValues,
          marca: valores.marca,
          modelo: valores.modelo,
          año: valores.anio,
          version: valores.version,
          nombre: valores.nombre.trim(),
          apellido: valores.apellido.trim(),
          email: valores.email.toLowerCase(),
          telefono: {
            prefijo: isUY ? "9" : valores.prefijo,
            numero: valores.numero,
          },
          fechaNac: {
            dia: valores.dia.padStart(2, "0"),
            mes: valores.mes.padStart(2, "0"),
            año: valores.anioNac,
          },
          genero: valores.genero,
          provincia: valores.provincia,
          localidad: valores.localidad,
          codigoPostal: viaBff ? valores.codigoPostal : cotizacionValues.codigoPostal,
          cuandoContrata: valores.cuandoContrata,
          // Camino BFF: el recaptcha se mintea fresco en el submit de
          // `contacto`, no se guarda un token single-use en el store.
          captchaToken: viaBff ? undefined : token,
          cotizaciones: [],
        });
        resetEmisionValues();
        nav(rutaResultado(rubro, isUY, valores.cuandoContrata?.value));
      });
    });
  };

  /** Volver al paso 1 (patente en Auto, vehículo en Moto) sin perder lo cargado. El asesor vuelve a /auto/vendedor. */
  const volver = () => nav(inicioCotizacion(rubro, cotizacionValues.landingUrl, compania));

  const volverAPatente = () => {
    setCotizacionValues({
      ...cotizacionValues,
      marca: undefined,
      modelo: undefined,
      version: undefined,
      año: undefined,
      patente: undefined,
      esAntiguo: undefined,
    });
    nav(inicioCotizacion("auto", cotizacionValues.landingUrl, compania));
  };

  const noEsMiVehiculo = () => {
    setCotizacionValues({
      ...cotizacionValues,
      marca: undefined,
      modelo: undefined,
      version: undefined,
      año: undefined,
      patente: undefined,
      esAntiguo: undefined,
    });
    cambiar({
      marca: undefined,
      modelo: undefined,
      version: undefined,
      anio: undefined,
    });
  };

  const colorEmpresa =
    config.origen !== "SEGURARSE"
      ? { selected: config.colors.accent, hover: config.colors.tertiary }
      : undefined;
  const bordeEmpresa =
    config.origen !== "SEGURARSE" ? config.colors.accent : undefined;

  const inputTexto = (
    campo: "nombre" | "apellido" | "email",
    etiqueta: string,
    placeHolder: string
  ) => (
    <div className="col-12 col-md-6" data-campo={campo}>
      <Etiqueta>{conAsterisco(etiqueta)}</Etiqueta>
      <SegInput
        label=""
        name={campo}
        id={campo}
        type="text"
        width={100}
        placeHolder={placeHolder}
        value={valores[campo]}
        onChange={(e) => cambiar({ [campo]: e.target.value })}
        onBlur={() => revisar(campo)}
        onKeyDown={(event) => {
          if (event.key === " " && !valores[campo]) event.preventDefault();
        }}
        inputMode={campo === "email" ? "email" : "text"}
        leyendaError={errores[campo]}
        borderColor={
          errores[campo] ? "var(--bs-form-invalid-border-color)" : bordeEmpresa
        }
        focusBorderColor={
          errores[campo]
            ? "var(--bs-form-invalid-border-color)"
            : bordeEmpresa || "#2c7dfd"
        }
      />
      {!errores[campo] && <MensajeError />}
    </div>
  );

  /**
   * Camino BFF: la localidad es texto libre. Se guarda igual que el select
   * (como SegSelectOption) para no cambiar el resto del flujo ni el store.
   */
  const inputLocalidadLibre = () => (
    <div className="col-12 col-md-6" data-campo="localidad">
      <Etiqueta>{conAsterisco("Localidad")}</Etiqueta>
      <SegInput
        label=""
        name="localidad"
        id="localidad"
        type="text"
        width={100}
        placeHolder="Ej: San Isidro"
        value={valores.localidad?.value || ""}
        onChange={(e) =>
          cambiar({
            localidad: e.target.value
              ? { value: e.target.value, label: e.target.value }
              : undefined,
          })
        }
        onBlur={() => revisar("localidad")}
        leyendaError={errores.localidad}
        borderColor={
          errores.localidad
            ? "var(--bs-form-invalid-border-color)"
            : bordeEmpresa
        }
        focusBorderColor={
          errores.localidad
            ? "var(--bs-form-invalid-border-color)"
            : bordeEmpresa || "#2c7dfd"
        }
      />
      {!errores.localidad && <MensajeError />}
    </div>
  );

  /** Camino BFF: el código postal (4 dígitos) es obligatorio. */
  const inputCodigoPostal = () => (
    <div className="col-12 col-md-6" data-campo="codigoPostal">
      <Etiqueta>{conAsterisco("Código postal")}</Etiqueta>
      <SegInput
        label=""
        name="codigoPostal"
        id="codigoPostal"
        type="text"
        width={100}
        placeHolder="Ej: 1425"
        inputMode="numeric"
        value={valores.codigoPostal || ""}
        onChange={(e) =>
          cambiar({ codigoPostal: e.target.value.replace(/\D/g, "").slice(0, 4) })
        }
        onBlur={() => revisar("codigoPostal")}
        leyendaError={errores.codigoPostal}
        borderColor={
          errores.codigoPostal
            ? "var(--bs-form-invalid-border-color)"
            : bordeEmpresa
        }
        focusBorderColor={
          errores.codigoPostal
            ? "var(--bs-form-invalid-border-color)"
            : bordeEmpresa || "#2c7dfd"
        }
      />
      {!errores.codigoPostal && <MensajeError />}
    </div>
  );

  const selectUbicacion = (
    campo: "provincia" | "localidad",
    etiqueta: string,
    opciones: SegSelectOption[] | undefined,
    placeholderDeshabilitado: string,
    cargando: boolean,
    onSelect: (opcion: SegSelectOption) => void
  ) => (
    <div className="col-12 col-md-6" data-campo={campo}>
      <Etiqueta>{conAsterisco(etiqueta)}</Etiqueta>
      <SegSelect
        name={campo}
        label=""
        options={opciones || []}
        selected={valores[campo] || null}
        setSelected={onSelect}
        width={"100%"}
        height={"md"}
        openDefault={false}
        loading={cargando}
        disabled={!opciones}
        placeholder={opciones ? "Seleccioná" : placeholderDeshabilitado}
        borderColor={errores[campo] ? "#d1424f" : undefined}
        hoverBorderColor={bordeEmpresa}
        focusBorderColor={bordeEmpresa}
        empresaColor={colorEmpresa}
      />
      <MensajeError>{errores[campo]}</MensajeError>
    </div>
  );

  return (
    <Contenedor>
      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <Columna>
          <Titulo>
            <h2>{textos.tituloDatos}</h2>
            <p>En un solo paso vas a acceder a todas las coberturas con sus precios.</p>
          </Titulo>

          {incluirVehiculo && (
            <>
              {patenteSinDatos && modoVehiculo === "manual" && (
                <AvisoPatente role="status">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" stroke="#1b62d8" strokeWidth="2" />
                    <path d="M12 11v5" stroke="#1b62d8" strokeWidth="2" strokeLinecap="round" />
                    <circle cx="12" cy="7.5" r="1.2" fill="#1b62d8" />
                  </svg>
                  <span>
                    No pudimos traer los datos de esa patente. Cargá el auto a
                    mano y seguimos.
                  </span>
                </AvisoPatente>
              )}
              <BloqueVehiculo
                rubro={rubro}
                config={config}
                isUY={isUY}
                modo={modoVehiculo}
                textoLibreForzado={vehiculoComoTexto}
                valores={valores}
                patente={cotizacionValues.patente}
                errores={errores}
                onCambio={cambiar}
                onRevisar={revisar}
                onOpcionesAnio={depurarAnio}
                onNoEsMiVehiculo={
                  modoVehiculo === "patente" ? noEsMiVehiculo : undefined
                }
                onVolverAPatente={
                  modoVehiculo === "manual" &&
                  cotizacionValues.parametrosIniciales?.dataCarHabilitado !==
                    false
                    ? volverAPatente
                    : undefined
                }
              />
              {vehiculoAntiguo && (
                <div className="alert alert-danger mb-0" role="alert">
                  <div className="fs-20 fw-medium">
                    Modelo anterior a {anioMaximo}
                  </div>
                  <div className="fs-16">
                    Lo sentimos, pero en este momento no tenemos coberturas
                    disponibles para ofrecerte. ¡Gracias por tu comprensión!
                  </div>
                  <button
                    type="button"
                    className="btn btn-link p-0 mt-2"
                    onClick={volverAPatente}
                  >
                    Cotizar otro auto
                  </button>
                </div>
              )}
            </>
          )}

          <FormProvider {...methods}>
            {/* ── sobre vos ── */}
            <Card>
              <Grupo>
                {/* Para el asesor el nombre del grupo queda solo para lectores de pantalla. */}
                {textos.leyendaPersonaVisible ? (
                  <Leyenda>{textos.leyendaPersona}</Leyenda>
                ) : (
                  <legend className="visually-hidden">{textos.leyendaPersona}</legend>
                )}
                <div className="row">
                  {inputTexto("nombre", "Nombre", "Ej: Juan")}
                  {inputTexto("apellido", "Apellido", "Ej: López")}

                  <div className="col-12 col-md-6" data-campo="fechaNac">
                    <Etiqueta>
                      {conAsterisco("Fecha de nacimiento")}
                      <SegTooltip text="Influye en el precio." />
                    </Etiqueta>
                    <SegInput
                      label=""
                      name="fechaNac"
                      type="text"
                      inputMode="numeric"
                      width={100}
                      placeHolder="DD/MM/AAAA"
                      value={fechaValor}
                      onChange={(e) => cambiarFecha(e.target.value)}
                      onBlur={() => revisar("fechaNac")}
                      borderColor={
                        errores.fechaNac
                          ? "var(--bs-form-invalid-border-color)"
                          : bordeEmpresa
                      }
                      focusBorderColor={
                        errores.fechaNac
                          ? "var(--bs-form-invalid-border-color)"
                          : bordeEmpresa || "#2c7dfd"
                      }
                    />
                    <MensajeError>{errores.fechaNac}</MensajeError>
                  </div>

                  <div className="col-12 col-md-6" data-campo="genero">
                    <Etiqueta>
                      {conAsterisco("Género")}
                      <SegTooltip
                        text="Las compañías aseguradoras lo solicitan."
                        placement="bottom-end"
                      />
                    </Etiqueta>
                    <SegSelect
                      name="genero"
                      label=""
                      options={GENEROS}
                      selected={valores.genero || null}
                      setSelected={(opcion) => cambiar({ genero: opcion })}
                      width={"100%"}
                      height={"md"}
                      openDefault={false}
                      placeholder="Seleccioná"
                      borderColor={errores.genero ? "#d1424f" : undefined}
                      hoverBorderColor={bordeEmpresa}
                      focusBorderColor={bordeEmpresa}
                      empresaColor={colorEmpresa}
                    />
                    <MensajeError>{errores.genero}</MensajeError>
                  </div>
                </div>
              </Grupo>
            </Card>

            {/* ── contacto ── */}
            <Card>
              <Grupo>
                <Leyenda>{textos.leyendaContacto}</Leyenda>
                <div className="row">
                  {inputTexto(
                    "email",
                    "Email",
                    isUY ? "Ej: nombre@dominio.com.uy" : "Ej: nombre@dominio.com.ar"
                  )}

                  <div className="col-12 col-md-6" data-campo="telefono">
                    <div className="row gx-2">
                      {!isUY && (
                        <div className="col-4">
                          <Etiqueta>{conAsterisco("Cód. Área")}</Etiqueta>
                          <SegInput
                            isGroup
                            labelGroup="0"
                            labelGroupColor={bordeEmpresa}
                            label=""
                            name="prefijo"
                            type="text"
                            inputMode="numeric"
                            width={100}
                            placeHolder="11"
                            value={valores.prefijo}
                            onChange={(e) =>
                              cambiar({
                                prefijo: e.target.value
                                  .replace(/[^0-9]/g, "")
                                  .slice(0, 4),
                              })
                            }
                            onBlur={() => revisar("telefono")}
                            borderColor={
                              errores.telefono
                                ? "var(--bs-form-invalid-border-color)"
                                : bordeEmpresa
                            }
                            focusBorderColor={
                              errores.telefono
                                ? "var(--bs-form-invalid-border-color)"
                                : bordeEmpresa || "#2c7dfd"
                            }
                          />
                          <MensajeError>
                            {errores.telefono?.includes("Cód")
                              ? errores.telefono
                              : ""}
                          </MensajeError>
                        </div>
                      )}
                      <div className={isUY ? "col-12" : "col-8"}>
                        <Etiqueta>{conAsterisco(isUY ? "Número de Celular" : "Número")}</Etiqueta>
                        <SegInput
                          isGroup
                          labelGroup={isUY ? "9" : "15"}
                          labelGroupColor={bordeEmpresa}
                          label=""
                          name="numero"
                          type="text"
                          inputMode="numeric"
                          width={100}
                          placeHolder="33669955"
                          value={valores.numero}
                          onChange={(e) =>
                            cambiar({
                              numero: e.target.value
                                .replace(/[^0-9]/g, "")
                                .slice(0, 9),
                            })
                          }
                          onBlur={() => revisar("telefono")}
                          borderColor={
                            errores.telefono
                              ? "var(--bs-form-invalid-border-color)"
                              : bordeEmpresa
                          }
                          focusBorderColor={
                            errores.telefono
                              ? "var(--bs-form-invalid-border-color)"
                              : bordeEmpresa || "#2c7dfd"
                          }
                        />
                        <MensajeError>
                          {errores.telefono && !errores.telefono.includes("Cód")
                            ? errores.telefono
                            : ""}
                        </MensajeError>
                      </div>
                    </div>
                    <Ayuda>
                      {isUY
                        ? "Número sin el 0 y sin el 9. En total, 8 dígitos."
                        : "Cód. de área sin 0 y número sin 15. En total, 10 dígitos."}
                    </Ayuda>
                  </div>
                </div>
              </Grupo>
            </Card>

            {/* ── dónde circula ── */}
            <Card>
              <Grupo>
                <Leyenda>{textos.leyendaUbicacion}</Leyenda>
                <div className="row">
                  {selectUbicacion(
                    "provincia",
                    isUY ? "Departamento" : "Provincia",
                    provincias,
                    "Cargando…",
                    !provincias,
                    (opcion) =>
                      cambiar({ provincia: opcion, localidad: undefined })
                  )}
                  {viaBff
                    ? inputLocalidadLibre()
                    : selectUbicacion(
                        "localidad",
                        "Localidad",
                        localidades,
                        isUY
                          ? "Elegí el departamento primero"
                          : "Elegí la provincia primero",
                        cargandoLocalidades,
                        (opcion) => cambiar({ localidad: opcion })
                      )}
                  {viaBff && inputCodigoPostal()}
                </div>
              </Grupo>
            </Card>
          </FormProvider>

          {errorGeneral && (
            <div className="alert alert-danger mb-0" role="alert">
              {errorGeneral}
            </div>
          )}
        </Columna>

        <Cierre>
          <CierreInterno>
            <SegButton
              label="Volver"
              type="button"
              onClick={volver}
              disabled={false}
              style={{ minWidth: "auto" }}
              bgColor="transparent"
              textColor="#4a5468"
              borderColor="transparent"
              hoverBgColor="#eef4ff"
              hoverTextColor="#1b62d8"
              hoverBorderColor="transparent"
            />
            <SegButton
              label="Ver precios"
              type="submit"
              style={{ minWidth: "220px" }}
              loading={enviando}
              spinnerSize="small"
              disabled={vehiculoAntiguo || enviando || !completo}
              bgColor={
                config.origen !== "SEGURARSE" ? config.colors.primary : undefined
              }
              hoverBgColor={
                config.origen !== "SEGURARSE" ? config.colors.primary : undefined
              }
              hoverBorderColor={
                config.origen !== "SEGURARSE" ? config.colors.accent : undefined
              }
              hoverOpacity={config.origen !== "SEGURARSE"}
            />
          </CierreInterno>
        </Cierre>
      </form>
    </Contenedor>
  );
};
